<template>
  <v-container v-if="localFactory" class="pa-0 workflow-editor-shell" fluid>
    <v-row class="workflow-header-row ma-0" density="compact">
      <v-col cols="6" class="pa-0 pr-2">
        <v-text-field
          class="my-0 mt-2"
          label="Factory Name"
          v-model="localFactory.name"
          @update:modelValue="scheduleTextFieldSave"
          @blur="saveTextFieldsNow"
          variant="outlined"
          density="compact"
        />
      </v-col>
      <v-col cols="6" class="pa-0 text-right d-flex align-center justify-end">
        <v-btn color="primary" density="compact" @click="runFactory" :loading="isRunning">Run Factory</v-btn>
      </v-col>
    </v-row>

    <div class="workflow-tabs-row mb-1">
      <v-tabs v-model="activeFactoryTab" density="compact" class="workflow-subtabs" color="primary">
        <v-tab value="runs" density="compact">Runs</v-tab>
        <v-tab value="config" density="compact">Config</v-tab>
        <v-tab value="schedules" density="compact">Schedules</v-tab>
      </v-tabs>
    </div>

    <v-window v-model="activeFactoryTab" class="workflow-editor-window">
      <v-window-item value="config" class="workflow-window-item">
        <div class="workflow-tab-scroll">
          <v-row density="compact" class="factory-row-tight mt-0 mb-4 mx-0">
            <v-col cols="12" class="pa-0">
              <v-textarea
                class="my-0 mt-2"
                label="Description"
                v-model="localFactory.description"
                @update:modelValue="scheduleTextFieldSave"
                @blur="saveTextFieldsNow"
                auto-grow
                rows="2"
                variant="outlined"
                density="compact"
                hide-details
              />
            </v-col>
          </v-row>

          <v-row density="compact" class="factory-row-tight mt-0 mb-4 mx-0 factory-controls-row">
            <v-col cols="4" class="pa-0">
              <v-checkbox
                v-model="localFactory.continuous"
                label="Continuous"
                color="primary"
                density="compact"
                hide-details
                class="my-0"
                @update:modelValue="saveNow"
              />
            </v-col>
            <v-col cols="4" class="pa-0 pr-2">
              <v-checkbox
                v-model="localFactory.limitTotalInvocations"
                label="Limit Total Factory Runs"
                color="primary"
                density="compact"
                hide-details
                class="my-0"
                @update:modelValue="saveNow"
              />
            </v-col>
            <v-col cols="4" class="pa-0">
              <v-number-input
                v-model="localFactory.maxTotalInvocations"
                label="Max Factory Runs"
                :min="1"
                :max="100000"
                control-variant="stacked"
                variant="outlined"
                density="compact"
                hide-details
                class="my-0"
                :disabled="localFactory.limitTotalInvocations !== true"
                @update:modelValue="saveNow"
              />
            </v-col>
          </v-row>

          <v-alert v-if="localFactory.continuous" type="warning" density="compact" variant="tonal" class="my-1 py-1">
            This factory is continuous. It will execute until paused or cancelled. If the total-run cap is enabled, it stops after the configured number of completed full passes when the last step finishes. Per-step limits still apply separately.
          </v-alert>

          <v-row density="compact" class="factory-row-tight mt-0 mb-4 mx-0">
            <v-col cols="12" class="pa-0">
              <v-textarea
                v-model="manualInitialCarryPayload"
                class="my-0 mt-2"
                label="Initial Payload"
                hint="This is sent into the first step when you run the factory manually."
                persistent-hint
                auto-grow
                rows="2"
                variant="outlined"
                density="compact"
              />
            </v-col>
          </v-row>

          <div class="d-flex align-center justify-space-between mb-1 step-toolbar">
            <div class="text-overline">Steps ({{ localFactory.steps.length }})</div>
            <!-- <v-btn size="small" variant="outlined" prepend-icon="mdi-plus" class="px-3" @click="addStep">Add Step</v-btn> -->
          </div>

          <v-card
            v-for="(step, index) in localFactory.steps"
            :key="step.stepId"
            variant="outlined"
            class="mb-8"
          >
            <v-card-title class="d-flex align-center py-2 px-3">
              <span class="text-subtitle-2">{{ step.name || `Step ${index + 1}` }}</span>
              <v-chip size="x-small" density="compact" class="ml-2" color="primary" variant="tonal">{{ step.kind }}</v-chip>
              <v-spacer />
              <v-btn size="x-small" density="compact" variant="text" icon="mdi-arrow-up" :disabled="index === 0" @click="moveStep(index, -1)" />
              <v-btn size="x-small" density="compact" variant="text" icon="mdi-arrow-down" :disabled="index === localFactory.steps.length - 1" @click="moveStep(index, 1)" />
              <v-btn size="x-small" density="compact" variant="text" icon="mdi-delete-outline" color="error" @click="confirmStepDelete(index)" />
            </v-card-title>
            <v-divider />
            <v-card-text class="pa-2 mx-1">
              <v-row density="compact" class="factory-row-tight ma-0">
                <v-col cols="3" class="py-2 pr-2 pl-0">
                  <v-text-field v-model="step.name" label="Step Name" hide-details variant="outlined" density="compact" class="my-0 mt-2" @update:modelValue="scheduleLightSave" />
                </v-col>
                <v-col cols="2" class="py-2 px-1">
                  <v-select
                    :model-value="step.kind"
                    :items="stepKindItems"
                    label="Kind"
                    variant="outlined"
                    density="compact"
                    hide-details
                    class="my-0 mt-2"
                    @update:modelValue="onStepKindValueChanged(step, $event)"
                  />
                </v-col>
                <v-col cols="7" class="py-2 pr-0 pl-2">
                  <template v-if="step.kind === 'agent'">
                    <v-select
                      v-model="step.agentRef!.agentRef"
                      :items="agentSelectItems"
                      item-title="title"
                      item-value="value"
                      label="Agent"
                      variant="outlined"
                      density="compact"
                      hide-details
                      class="my-0 mt-2"
                      @update:modelValue="saveNow"
                    />
                  </template>
                  <template v-else>
                    <v-select
                      v-model="step.workflowRef!.workflowConfigId"
                      :items="workflowSelectItems"
                      item-title="title"
                      item-value="value"
                      label="Workflow"
                      variant="outlined"
                      density="compact"
                      hide-details
                      class="my-0 mt-2"
                      @update:modelValue="saveNow"
                    />
                  </template>
                </v-col>
              </v-row>

              <template v-if="step.kind === 'workflow'">
                <v-row density="compact" class="factory-row-tight ma-0">
                  <v-col cols="12" class="pa-0">
                    <div class="factory-section-shell mt-2 mb-2">
                      <div class="factory-section-header pa-3 d-flex justify-space-between align-center">
                        <div class="factory-section-title">Payload Schema</div>
                        <v-btn-toggle
                          class="factory-section-toggle"
                          :model-value="schemaViewModes[step.stepId] || 'visual'"
                          mandatory
                          density="compact"
                          variant="outlined"
                          @update:modelValue="onSchemaViewModeChanged(step.stepId, $event)"
                        >
                          <v-btn value="visual">Visual</v-btn>
                          <v-btn value="json" :disabled="!!schemaErrors[step.stepId]">Raw</v-btn>
                        </v-btn-toggle>
                      </div>

                      <div class="factory-section-body">
                        <div v-if="(schemaViewModes[step.stepId] || 'visual') === 'visual'" class="visual-payload-editor factory-schema-visual-editor">
                          <div class="text-caption text-medium-emphasis mb-3">
                            Start with the example field below and adjust the type, requirement, or description for the workflow input you expect.
                          </div>
                          <FactorySchemaNode
                            v-for="(field, fieldIndex) in schemaFieldDrafts[step.stepId] || []"
                            :key="field.id"
                            :field="field"
                            :depth="0"
                            @update="updateRootSchemaField(step, fieldIndex, $event)"
                            @delete="removeRootSchemaField(step, fieldIndex)"
                          />
                          <v-btn size="small" variant="tonal" prepend-icon="mdi-plus" @click="addSchemaField(step)">Add Field</v-btn>
                        </div>

                        <v-textarea
                          v-else
                          :model-value="schemaDrafts[step.stepId] || ''"
                          label="Payload Schema (JSON)"
                          auto-grow
                          rows="6"
                          variant="outlined"
                          density="compact"
                          class="my-0"
                          :error-messages="schemaErrors[step.stepId] ? [schemaErrors[step.stepId]] : []"
                          @update:modelValue="onSchemaDraftChanged(step.stepId, $event)"
                          @blur="commitSchemaDraft(step)"
                        />
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </template>

              <v-expansion-panels density="compact" variant="accordion" elevation="0" class="mt-0 mb-2 factory-options-panels">
                <v-expansion-panel class="factory-options-panel">
                  <v-expansion-panel-title class="expansion-panel-title pa-3">Options</v-expansion-panel-title>
                  <v-expansion-panel-text>
                    <template v-if="step.kind === 'agent'">
                      <v-row density="compact" class="factory-row-tight ma-0">
                        <v-col cols="8" class="pa-0 pr-2">
                          <v-textarea
                            v-model="step.agentRef!.promptOverride"
                            label="Prompt Override"
                            auto-grow
                            rows="2"
                            variant="outlined"
                            density="compact"
                            hide-details
                            class="my-0 mt-2"
                            @update:modelValue="scheduleTextFieldSave"
                            @blur="saveTextFieldsNow"
                          />
                        </v-col>
                        <v-col cols="4" class="pa-0 pl-2">
                          <v-checkbox
                            v-model="step.limitStepInvocations"
                            label="Limit Step Runs"
                            color="primary"
                            density="compact"
                            hide-details
                            class="my-0 mt-2"
                            @update:modelValue="saveNow"
                          />
                          <v-number-input
                            v-model="step.maxStepInvocations"
                            label="Max Step Invocations"
                            :min="1"
                            :max="10000"
                            control-variant="stacked"
                            variant="outlined"
                            density="compact"
                            hide-details
                            class="my-0 mt-2"
                            :disabled="step.limitStepInvocations !== true"
                            @update:modelValue="scheduleLightSave"
                          />
                        </v-col>
                      </v-row>
                    </template>

                    <template v-else>
                      <v-row density="compact" class="factory-row-tight ma-0">
                        <v-col cols="8" class="pa-0 pr-2">
                          <v-select
                            v-model="step.workflowRef!.fixerAgentRef"
                            :items="agentSelectItems"
                            item-title="title"
                            item-value="value"
                            label="Fixer Agent"
                            variant="outlined"
                            density="compact"
                            hide-details
                            class="my-0 mt-2"
                            clearable
                            @update:modelValue="saveNow"
                          />
                          <v-number-input
                            v-model="step.workflowRef!.maxRepairAttempts"
                            label="Repair Attempts"
                            :min="0"
                            :max="5"
                            control-variant="stacked"
                            variant="outlined"
                            density="compact"
                            hide-details
                            class="my-0 mt-2"
                            @update:modelValue="saveNow"
                          />
                        </v-col>
                        <v-col cols="4" class="pa-0 pl-2">
                          <v-checkbox
                            v-model="step.limitStepInvocations"
                            label="Limit Step Runs"
                            color="primary"
                            density="compact"
                            hide-details
                            class="my-0 mt-2"
                            @update:modelValue="saveNow"
                          />
                          <v-number-input
                            v-model="step.maxStepInvocations"
                            label="Max Step Invocations"
                            :min="1"
                            :max="10000"
                            control-variant="stacked"
                            variant="outlined"
                            density="compact"
                            hide-details
                            class="my-0 mt-2"
                            :disabled="step.limitStepInvocations !== true"
                            @update:modelValue="scheduleLightSave"
                          />
                        </v-col>
                      </v-row>
                    </template>
                  </v-expansion-panel-text>
                </v-expansion-panel>
              </v-expansion-panels>

              <v-row density="compact" class="factory-row-tight mt-4 mb-0 mx-0">
                <v-col cols="4" class="pa-0">
                  <v-select
                    :model-value="step.transition.kind"
                    :items="transitionKindItems"
                    label="Transition"
                    variant="outlined"
                    density="compact"
                    hide-details
                    class="my-0"
                    @update:modelValue="onTransitionKindValueChanged(step, $event)"
                  />
                </v-col>
                <v-col v-if="step.transition.kind === 'loop_to_index'" cols="4" class="pa-0 pl-2">
                  <v-select
                    :model-value="step.transition.targetIndex"
                    :items="loopTargetItems"
                    label="Loop Target"
                    variant="outlined"
                    density="compact"
                    hide-details
                    class="my-0"
                    @update:modelValue="onLoopTargetChanged(step, Number($event))"
                  />
                </v-col>
                <v-col :cols="step.transition.kind === 'loop_to_index' ? 4 : 8" class="pa-0 d-flex justify-end align-end">
                  <v-btn size="small" variant="outlined" prepend-icon="mdi-plus" class="px-3" @click="insertStepAt(index + 1)">
                    Add Step
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </div>
      </v-window-item>

      <v-window-item value="runs" class="workflow-window-item">
        <div class="workflow-result-tab">
          <FactoryRunBrowser
            v-if="localFactory"
            :factory-id="localFactory.id"
            :factory-name="localFactory.name"
            :live-run-state="factoryStreamState"
            @pause="pauseFactory"
            @resume="resumeFactory"
            @cancel="cancelFactory"
          />
        </div>
      </v-window-item>

      <v-window-item value="schedules" class="workflow-window-item">
        <div class="workflow-tab-scroll">
          <div class="d-flex align-center justify-space-between mb-2">
            <div class="text-overline">Schedules</div>
            <v-btn size="small" variant="outlined" prepend-icon="mdi-plus" class="px-3" @click="createSchedule">Add Schedule</v-btn>
          </div>
          <FactoryScheduleCard
            v-for="schedule in schedules"
            :key="schedule.id"
            :schedule="schedule"
            :factory-id="localFactory.id"
            @save="saveSchedule"
            @toggle="toggleSchedule"
            @delete="deleteSchedule"
          />
          <FactoryScheduleCard
            v-if="draftSchedule"
            :schedule="draftSchedule"
            :factory-id="localFactory.id"
            :is-new="true"
            @save="saveSchedule"
            @cancel-new="draftSchedule = null"
          />
        </div>
      </v-window-item>
    </v-window>

    <ConfirmationDialog
      v-model="showDeleteConfirmation"
      title="Confirm Step Deletion"
      message="Are you sure you want to delete this step?"
      @confirm="deletePendingStep"
      @cancel="clearPendingStepDelete"
    />
  </v-container>
</template>

<script lang="ts">
import { debounce, isEqual, DebouncedFunc } from 'lodash'
import { Options, Vue } from 'vue-class-component'
import { mapActions, mapGetters } from 'vuex'
import { nanoid } from 'nanoid'
import ConfirmationDialog from '@/components/dialogs/ConfirmationDialog.vue'
import FactoryRunBrowser from '@/components/factories/FactoryRunBrowser.vue'
import FactorySchemaNode from '@/components/factories/FactorySchemaNode.vue'
import FactoryScheduleCard from '@/components/factories/FactoryScheduleCard.vue'
import { useFactoryStream, type FactoryStreamState } from '@/composables/useFactoryStream'
import type { FactoryConfig, FactoryScheduleDraft, FactoryScheduleRecord, FactoryStepConfig, NewFactorySchedulePayload } from '@/types/factories'
import type { AccessibleAgentItem } from '@/store/agents'
import type { WorkflowConfig } from '@/types/workflows'
import { formatOwnedAgentRef, isCanonicalAgentRef } from '@/utils/agentRefs'
import {
  createSchemaFieldDraft,
  isVisualizableFactorySchema,
  schemaFieldsFromSchema,
  SchemaFieldDraft,
  schemaFromFields
} from '@/utils/factorySchemaTree'

function normalizeFactoryForEditing(config: FactoryConfig | null | undefined): Omit<FactoryConfig, 'updatedAt' | 'createdAt' | 'userId'> | null {
  if (!config) return null
  const { updatedAt: _updatedAt, createdAt: _createdAt, userId: _userId, ...rest } = config
  return {
    ...rest,
    limitTotalInvocations: rest.limitTotalInvocations ?? false,
    steps: (rest.steps || []).map((step) => ({
      ...step,
      limitStepInvocations: step.limitStepInvocations ?? false
    }))
  }
}

@Options({
  props: {
    id: { type: String, required: true },
    isActive: { type: Boolean, default: false }
  },
  components: { FactoryRunBrowser, FactoryScheduleCard, ConfirmationDialog, FactorySchemaNode },
  computed: {
    ...mapGetters(['getFactoryById', 'agentConfigsList', 'workflowList', 'factorySchedulesByFactory', 'accessibleAgentItems'])
  },
  methods: {
    ...mapActions([
      'saveFactory',
      'fetchFactoryRunsForFactory',
      'fetchFactorySchedules',
      'saveFactorySchedule',
      'deleteFactorySchedule',
      'toggleFactorySchedule'
    ])
  },
  watch: {
    factory: {
      handler(newConfig: FactoryConfig | undefined) {
        if (!newConfig) return
        const normalizedIncoming = normalizeFactoryForEditing(newConfig)
        const normalizedLocal = normalizeFactoryForEditing(this.localFactory)
        if (!isEqual(normalizedIncoming, normalizedLocal)) {
          this.localFactory = {
            ...JSON.parse(JSON.stringify(newConfig)),
            limitTotalInvocations: newConfig.limitTotalInvocations ?? false,
            steps: (newConfig.steps || []).map((step) => ({
              ...JSON.parse(JSON.stringify(step)),
              limitStepInvocations: step.limitStepInvocations ?? false
            }))
          }
          this.normalizeFactoryCompatibility()
          this.syncSchemaDrafts()
          if (!normalizedLocal) {
            this.syncDefaultActiveTab()
          }
        }
      },
      immediate: true,
      deep: true
    },
    factoryStreamState: {
      handler(newState: FactoryStreamState) {
        this.isRunning = newState.status === 'running' || newState.status === 'queued'
        if (newState.status !== 'idle' || newState.runId || newState.stepRuns.length > 0 || newState.error) {
          this.activeFactoryTab = 'runs'
        } else {
          this.syncDefaultActiveTab()
        }
      },
      deep: true
    },
    isActive(value: boolean) {
      if (!value) return
      this.syncDefaultActiveTab()
      void this.fetchFactorySchedules()
    }
  }
})
export default class FactoryEditor extends Vue {
  id!: string
  isActive!: boolean

  getFactoryById!: (id: string) => FactoryConfig | undefined
  accessibleAgentItems!: AccessibleAgentItem[]
  workflowList!: WorkflowConfig[]
  factorySchedulesByFactory!: (factoryId: string) => FactoryScheduleRecord[]

  saveFactory!: (config: FactoryConfig) => Promise<boolean>
  fetchFactoryRunsForFactory!: (params: { factoryId: string; limit?: number; offset?: number; append?: boolean }) => Promise<boolean>
  fetchFactorySchedules!: () => Promise<boolean>
  saveFactorySchedule!: (payload: NewFactorySchedulePayload | FactoryScheduleRecord) => Promise<boolean>
  deleteFactorySchedule!: (scheduleId: string) => Promise<boolean>
  toggleFactorySchedule!: (scheduleId: string) => Promise<boolean>

  private factoryStream: ReturnType<typeof useFactoryStream> | null = null
  localFactory: FactoryConfig | null = null
  activeFactoryTab: 'runs' | 'config' | 'schedules' = 'config'
  isRunning = false
  save!: DebouncedFunc<() => void>
  saveTextFields!: DebouncedFunc<() => void>
  schemaDrafts: Record<string, string> = {}
  schemaErrors: Record<string, string> = {}
  schemaViewModes: Record<string, 'visual' | 'json'> = {}
  schemaFieldDrafts: Record<string, SchemaFieldDraft[]> = {}
  draftSchedule: FactoryScheduleDraft | null = null
  manualInitialCarryPayload = 'execute'
  showDeleteConfirmation = false
  pendingStepDeleteIndex: number | null = null

  stepKindItems = [
    { title: 'Agent', value: 'agent' },
    { title: 'Workflow', value: 'workflow' }
  ]

  transitionKindItems = [
    { title: 'Next', value: 'next' },
    { title: 'Loop', value: 'loop_to_index' },
    { title: 'Stop', value: 'stop' }
  ]

  get factory(): FactoryConfig | undefined {
    return this.getFactoryById(this.id)
  }

  get factoryStreamState(): FactoryStreamState {
    return this.factoryStream?.state ?? {
      status: 'idle',
      runId: null,
      run: null,
      stepRuns: [],
      error: null
    }
  }

  get persistedRunsCount(): number {
    return (this.$store.getters.factoryRunsByFactory?.(this.id) || []).length
  }

  get schedules(): FactoryScheduleRecord[] {
    return this.factorySchedulesByFactory?.(this.id) || []
  }

  get agentSelectItems() {
    return this.accessibleAgentItems.map((agent) => ({ title: agent.title, value: agent.ref }))
  }

  get workflowSelectItems() {
    return this.workflowList.map((workflow) => ({ title: workflow.name, value: workflow.id }))
  }

  get loopTargetItems() {
    return (this.localFactory?.steps || []).map((step, index) => ({ title: `${index + 1}. ${step.name}`, value: index }))
  }

  created() {
    this.factoryStream = useFactoryStream(this.$store)
    this.save = debounce(() => {
      this.persistFactoryEdits()
    }, 500)
    this.saveTextFields = debounce(() => {
      this.persistFactoryEdits()
    }, 5000)
  }

  async mounted() {
    await this.fetchFactoryRunsForFactory({ factoryId: this.id, limit: 20, offset: 0, append: false })
    await this.fetchFactorySchedules()
    if (this.localFactory && this.factoryStream) {
      const resumed = await this.factoryStream.resume(this.localFactory.id, { onlyActive: true })
      if (resumed) {
        this.isRunning = this.factoryStreamState.status === 'running' || this.factoryStreamState.status === 'queued'
        this.activeFactoryTab = 'runs'
      } else {
        this.syncDefaultActiveTab()
      }
    }
  }

  beforeUnmount() {
    this.save.flush()
    this.saveTextFields.flush()
  }

  syncDefaultActiveTab() {
    const hasActiveRun =
      this.factoryStreamState.status !== 'idle' ||
      Boolean(this.factoryStreamState.runId) ||
      this.factoryStreamState.stepRuns.length > 0 ||
      Boolean(this.factoryStreamState.error)

    this.activeFactoryTab = hasActiveRun || this.persistedRunsCount > 0 ? 'runs' : 'config'
  }

  syncSchemaDrafts() {
    const drafts: Record<string, string> = {}
    const viewModes: Record<string, 'visual' | 'json'> = {}
    const fieldDrafts: Record<string, SchemaFieldDraft[]> = {}
    ;(this.localFactory?.steps || []).forEach((step) => {
      if (step.kind === 'workflow') {
        const schema = step.workflowRef?.payloadSchema
        if (schema) {
          drafts[step.stepId] = JSON.stringify(schema, null, 2)
          if (isVisualizableFactorySchema(schema)) {
            fieldDrafts[step.stepId] = schemaFieldsFromSchema(schema)
            viewModes[step.stepId] = this.schemaViewModes[step.stepId] ?? 'visual'
          } else {
            fieldDrafts[step.stepId] = []
            viewModes[step.stepId] = 'json'
          }
        } else {
          const exampleSchema = this.buildExamplePayloadSchema()
          drafts[step.stepId] = JSON.stringify(exampleSchema, null, 2)
          fieldDrafts[step.stepId] = schemaFieldsFromSchema(exampleSchema)
          viewModes[step.stepId] = this.schemaViewModes[step.stepId] ?? 'visual'
        }
      }
      step.limitStepInvocations = step.limitStepInvocations ?? false
    })
    this.schemaDrafts = drafts
    this.schemaViewModes = viewModes
    this.schemaFieldDrafts = fieldDrafts
  }

  normalizeFactoryCompatibility() {
    if (!this.localFactory) return
    this.localFactory.steps = this.localFactory.steps.map((step) => {
      if (step.kind === 'agent' && step.agentRef) {
        if (
          (!step.agentRef.agentRef || !isCanonicalAgentRef(step.agentRef.agentRef)) &&
          typeof step.agentRef.agentId === 'string' &&
          step.agentRef.agentId.length > 0
        ) {
          step.agentRef.agentRef = formatOwnedAgentRef(step.agentRef.agentId)
        }
      }
      if (step.kind === 'workflow' && step.workflowRef) {
        if (
          (!step.workflowRef.fixerAgentRef || !isCanonicalAgentRef(step.workflowRef.fixerAgentRef)) &&
          typeof step.workflowRef.fixerAgentId === 'string' &&
          step.workflowRef.fixerAgentId.length > 0
        ) {
          step.workflowRef.fixerAgentRef = formatOwnedAgentRef(step.workflowRef.fixerAgentId)
        }
      }
      return step
    })
  }

  persistFactoryEdits() {
    if (this.localFactory) {
      this.reindexSteps()
      if (!this.canPersistFactory(this.localFactory)) {
        return
      }
      void this.saveFactory(this.localFactory)
    }
  }

  canPersistFactory(factory: FactoryConfig): boolean {
    return factory.steps.every((step) => this.isStepPersistable(step))
  }

  isStepPersistable(step: FactoryStepConfig): boolean {
    if (step.kind === 'agent') {
      return typeof step.agentRef?.agentRef === 'string' && step.agentRef.agentRef.trim().length > 0
    }
    if (step.kind === 'workflow') {
      return typeof step.workflowRef?.workflowConfigId === 'string' && step.workflowRef.workflowConfigId.trim().length > 0
    }
    return false
  }

  scheduleTextFieldSave() {
    this.saveTextFields()
  }

  saveTextFieldsNow() {
    this.saveTextFields.cancel()
    this.persistFactoryEdits()
  }

  scheduleLightSave() {
    this.save()
  }

  saveNow() {
    this.save.cancel()
    this.persistFactoryEdits()
  }

  addStep() {
    this.insertStepAt(this.localFactory?.steps.length ?? 0)
  }

  insertStepAt(index: number) {
    if (!this.localFactory) return
    this.localFactory.steps.splice(index, 0, {
      stepId: nanoid(),
      index,
      name: `Step ${index + 1}`,
      kind: 'agent',
      limitStepInvocations: false,
      agentRef: { agentRef: '' },
      maxStepInvocations: 1,
      transition: { kind: 'stop' }
    })
    this.reindexSteps()
  }

  confirmStepDelete(index: number) {
    this.pendingStepDeleteIndex = index
    this.showDeleteConfirmation = true
  }

  deletePendingStep() {
    if (this.pendingStepDeleteIndex == null || !this.localFactory) return
    this.localFactory.steps.splice(this.pendingStepDeleteIndex, 1)
    if (this.localFactory.steps.length === 0) {
      this.addStep()
    } else {
      this.reindexSteps()
      this.saveNow()
    }
    this.clearPendingStepDelete()
  }

  clearPendingStepDelete() {
    this.pendingStepDeleteIndex = null
    this.showDeleteConfirmation = false
  }

  moveStep(index: number, direction: -1 | 1) {
    if (!this.localFactory) return
    const target = index + direction
    if (target < 0 || target >= this.localFactory.steps.length) return
    const [step] = this.localFactory.steps.splice(index, 1)
    this.localFactory.steps.splice(target, 0, step)
    this.reindexSteps()
    this.saveNow()
  }

  reindexSteps() {
    if (!this.localFactory) return
    this.localFactory.steps = this.localFactory.steps.map((step, index) => ({ ...step, index }))
  }

  onStepKindChanged(step: FactoryStepConfig, kind: 'agent' | 'workflow') {
    step.kind = kind
    if (kind === 'agent') {
      step.agentRef = { agentRef: '' }
      delete step.workflowRef
      delete this.schemaDrafts[step.stepId]
      delete this.schemaErrors[step.stepId]
      delete this.schemaFieldDrafts[step.stepId]
      delete this.schemaViewModes[step.stepId]
    } else {
      const exampleSchema = this.buildExamplePayloadSchema()
      step.workflowRef = { workflowConfigId: '', payloadSchema: exampleSchema, maxRepairAttempts: 0 }
      delete step.agentRef
      this.schemaDrafts = { ...this.schemaDrafts, [step.stepId]: JSON.stringify(exampleSchema, null, 2) }
      this.schemaFieldDrafts = { ...this.schemaFieldDrafts, [step.stepId]: schemaFieldsFromSchema(exampleSchema) }
      this.schemaViewModes = { ...this.schemaViewModes, [step.stepId]: 'visual' }
    }
    step.limitStepInvocations = step.limitStepInvocations ?? false
  }

  onStepKindValueChanged(step: FactoryStepConfig, value: unknown) {
    if (value === 'agent' || value === 'workflow') {
      this.onStepKindChanged(step, value)
    }
  }

  onTransitionKindChanged(step: FactoryStepConfig, kind: 'next' | 'loop_to_index' | 'stop') {
    if (kind === 'loop_to_index') {
      step.transition = { kind, targetIndex: 0 }
    } else {
      step.transition = { kind }
    }
    this.saveNow()
  }

  onTransitionKindValueChanged(step: FactoryStepConfig, value: unknown) {
    if (value === 'next' || value === 'loop_to_index' || value === 'stop') {
      this.onTransitionKindChanged(step, value)
    }
  }

  onLoopTargetChanged(step: FactoryStepConfig, targetIndex: number) {
    step.transition = { kind: 'loop_to_index', targetIndex }
    this.saveNow()
  }

  updateSchemaDraft(stepId: string, value: string) {
    this.schemaDrafts = { ...this.schemaDrafts, [stepId]: value }
  }

  onSchemaDraftChanged(stepId: string, value: unknown) {
    this.updateSchemaDraft(stepId, typeof value === 'string' ? value : '')
  }

  setSchemaViewMode(stepId: string, value: 'visual' | 'json') {
    this.schemaViewModes = { ...this.schemaViewModes, [stepId]: value }
  }

  onSchemaViewModeChanged(stepId: string, value: unknown) {
    if (value === 'visual' || value === 'json') {
      this.setSchemaViewMode(stepId, value)
    }
  }

  addSchemaField(step: FactoryStepConfig) {
    const current = this.schemaFieldDrafts[step.stepId] || []
    this.schemaFieldDrafts = {
      ...this.schemaFieldDrafts,
      [step.stepId]: [...current, createSchemaFieldDraft('string')]
    }
    this.applyVisualSchema(step)
  }

  removeRootSchemaField(step: FactoryStepConfig, fieldIndex: number) {
    const current = this.schemaFieldDrafts[step.stepId] || []
    this.schemaFieldDrafts = {
      ...this.schemaFieldDrafts,
      [step.stepId]: current.filter((_, index) => index !== fieldIndex)
    }
    this.applyVisualSchema(step)
  }

  updateRootSchemaField(step: FactoryStepConfig, fieldIndex: number, field: SchemaFieldDraft) {
    const current = this.schemaFieldDrafts[step.stepId] || []
    this.schemaFieldDrafts = {
      ...this.schemaFieldDrafts,
      [step.stepId]: current.map((existing, index) => (index === fieldIndex ? field : existing))
    }
    this.applyVisualSchema(step)
  }

  applyVisualSchema(step: FactoryStepConfig) {
    if (step.kind !== 'workflow' || !step.workflowRef) return
    const fields = this.schemaFieldDrafts[step.stepId] || []
    const schema = schemaFromFields(fields)
    step.workflowRef.payloadSchema = schema
    this.schemaDrafts = { ...this.schemaDrafts, [step.stepId]: JSON.stringify(schema, null, 2) }
    this.schemaErrors = { ...this.schemaErrors, [step.stepId]: '' }
    this.scheduleLightSave()
  }

  commitSchemaDraft(step: FactoryStepConfig) {
    if (step.kind !== 'workflow' || !step.workflowRef) return
    const draft = this.schemaDrafts[step.stepId]?.trim()
    if (!draft) {
      step.workflowRef.payloadSchema = undefined
      this.schemaErrors = { ...this.schemaErrors, [step.stepId]: '' }
      this.saveNow()
      return
    }
    try {
      const parsed = JSON.parse(draft)
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw new Error('Schema must be a JSON object')
      }
      step.workflowRef.payloadSchema = parsed as Record<string, unknown>
      if (isVisualizableFactorySchema(step.workflowRef.payloadSchema)) {
        this.schemaFieldDrafts = {
          ...this.schemaFieldDrafts,
          [step.stepId]: schemaFieldsFromSchema(step.workflowRef.payloadSchema)
        }
        if (this.schemaViewModes[step.stepId] !== 'json') {
          this.schemaViewModes = { ...this.schemaViewModes, [step.stepId]: 'visual' }
        }
      } else {
        this.schemaViewModes = { ...this.schemaViewModes, [step.stepId]: 'json' }
      }
      this.schemaErrors = { ...this.schemaErrors, [step.stepId]: '' }
      this.saveNow()
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Invalid JSON schema'
      this.schemaErrors = { ...this.schemaErrors, [step.stepId]: message }
      this.$store.dispatch('updateSnackbar', { text: `Schema Error: ${message}`, color: 'error' }, { root: true })
    }
  }

  buildExamplePayloadSchema(): Record<string, unknown> {
    return {
      type: 'object',
      properties: {
        input: {
          type: 'string',
          description: 'Example input passed into the workflow step'
        }
      },
      required: ['input'],
      additionalProperties: false
    }
  }

  async runFactory() {
    if (!this.localFactory || !this.factoryStream) return
    this.save.flush()
    this.saveTextFields.flush()
    this.isRunning = true
    await this.factoryStream.execute(this.localFactory.id, this.manualInitialCarryPayload)
    this.activeFactoryTab = 'runs'
    this.isRunning = this.factoryStreamState.status === 'running' || this.factoryStreamState.status === 'queued'
  }

  async pauseFactory() {
    if (!this.factoryStream) return
    const result = await this.factoryStream.pause()
    if (result.run) {
      this.isRunning = result.run.status === 'running' || result.run.status === 'queued'
      return
    }
    this.isRunning = this.factoryStreamState.status === 'running' || this.factoryStreamState.status === 'queued'
  }

  async resumeFactory() {
    if (!this.factoryStream) return
    const result = await this.factoryStream.resumeRun()
    if (result.run) {
      this.isRunning = result.run.status === 'running' || result.run.status === 'queued'
      return
    }
    this.isRunning = this.factoryStreamState.status === 'running' || this.factoryStreamState.status === 'queued'
  }

  async cancelFactory() {
    if (!this.factoryStream) return
    const result = await this.factoryStream.cancel()
    if (result.run) {
      this.isRunning = result.run.status === 'running' || result.run.status === 'queued'
      return
    }
    this.isRunning = false
  }

  createSchedule() {
    if (!this.localFactory) return
    this.draftSchedule = {
      factoryConfigId: this.localFactory.id,
      label: '',
      startDate: Date.now(),
      timesToRun: [{ hour: 9, minute: 0 }],
      repeatInterval: 'daily',
      status: 'enabled',
      nextRunAt: Date.now(),
      initialCarryPayload: ''
    }
  }

  async saveSchedule(payload: NewFactorySchedulePayload | FactoryScheduleRecord) {
    const success = await this.saveFactorySchedule(payload)
    if (success) {
      this.draftSchedule = null
      await this.fetchFactorySchedules()
    }
  }

  async toggleSchedule(scheduleId: string) {
    await this.toggleFactorySchedule(scheduleId)
    await this.fetchFactorySchedules()
  }

  async deleteSchedule(scheduleId: string) {
    await this.deleteFactorySchedule(scheduleId)
    await this.fetchFactorySchedules()
  }
}
</script>

<style scoped lang="scss">
.workflow-result-tab,
.workflow-editor-window,
.workflow-window-item,
.workflow-tab-scroll {
  height: 100%;
  min-height: 0;
}

.workflow-tab-scroll {
  overflow-y: auto;
  padding-right: 8px;
}

.factory-section-shell {
  border: 1px solid rgba(0, 0, 0, 0.24);
  border-radius: 6px;
  background: transparent;
}

.factory-section-header {
  min-height: auto;
}

.factory-section-title {
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
}

.factory-section-toggle {
  margin-top: 4px;
}

.factory-section-body {
  padding: 0 16px 16px;
}

.factory-schema-visual-editor {
  padding: 0;
}

.factory-schema-field-row {
  flex-wrap: nowrap;
}

.factory-schema-field-row > :deep(.v-col) {
  min-width: 0;
}

.factory-schema-field-row__required,
.factory-schema-field-row__delete {
  flex: 0 0 auto;
}

.factory-schema-field-row__description {
  flex: 1 1 auto;
}

.factory-options-panels {
  background: transparent;
}

.factory-options-panels :deep(.v-expansion-panel) {
  background: transparent;
  border: 1px solid rgba(0, 0, 0, 0.24);
  border-radius: 6px;
  box-shadow: none;
}

.factory-options-panels :deep(.v-expansion-panel::after),
.factory-options-panels :deep(.v-expansion-panel::before) {
  display: none;
}

.factory-options-panels :deep(.v-expansion-panel-title) {
  min-height: 40px;
  padding: 0 16px;
}

.factory-options-panels :deep(.v-expansion-panel-title__overlay) {
  opacity: 0;
}

.factory-options-panels :deep(.v-expansion-panel-text__wrapper) {
  padding: 0 16px 16px;
}

</style>
